import React from 'react';
import { motion } from 'framer-motion';
import { Package, Edit3, Trash2, Star, TrendingDown, Box } from 'lucide-react';
import { formatStock, formatStockShort } from '../../utils/formatStock';

function getStockLevel(stock, minStock) {
  if (stock <= 0) return { color: 'bg-red-500', text: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/20', label: 'Out of Stock' };
  if (minStock && stock <= minStock) return { color: 'bg-red-400', text: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/20', label: 'Critical' };
  if (minStock && stock <= minStock * 2) return { color: 'bg-amber-400', text: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20', label: 'Low' };
  return { color: 'bg-emerald-400', text: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/20', label: 'In Stock' };
}

function getProgressPercent(stock, minStock) {
  if (!minStock || minStock === 0) return 100;
  const ratio = stock / (minStock * 3);
  return Math.min(100, Math.max(0, ratio * 100));
}

function getProfitColor(margin) {
  if (margin >= 30) return 'text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30';
  if (margin >= 15) return 'text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30';
  return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30';
}

export default function ProductCard({ product, onEdit, onDelete }) {
  const stock = product.stock || product.quantity || 0;
  const stockLevel = getStockLevel(stock, product.minStock);
  const profitPercent = product.purchasePrice && product.salePrice
    ? ((product.salePrice - product.purchasePrice) / product.salePrice) * 100
    : 0;
  const progressPercent = getProgressPercent(stock, product.minStock);
  const hasPackaging = product.piecesPerPack && product.packsPerBox;
  const readableStock = hasPackaging ? formatStock(stock, product.piecesPerPack, product.packsPerBox) : null;
  const shortStock = hasPackaging ? formatStockShort(stock, product.piecesPerPack, product.packsPerBox) : null;

  return (
    <motion.div
      whileHover={{ y: -4, scale: 1.01, boxShadow: '0 12px 30px rgba(0,0,0,0.08)' }}
      transition={{ duration: 0.2 }}
      className="group relative bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm"
    >
      <div className="relative h-40 bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
        {product.image ? (
          <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
        ) : (
          <Package size={48} className="text-gray-300 dark:text-gray-600" />
        )}

        {product.category && (
          <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-medium bg-white/90 dark:bg-gray-900/90 text-gray-700 dark:text-gray-300 rounded-full backdrop-blur-sm">
            {product.category}
          </span>
        )}

        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={(e) => { e.stopPropagation(); onEdit?.(product); }}
            className="p-1.5 rounded-lg bg-white dark:bg-gray-800 shadow-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            <Edit3 size={14} />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={(e) => { e.stopPropagation(); onDelete?.(product); }}
            className="p-1.5 rounded-lg bg-white dark:bg-gray-800 shadow-sm text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
          >
            <Trash2 size={14} />
          </motion.button>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 dark:bg-gray-600">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className={`h-full ${stockLevel.color}`}
          />
        </div>
      </div>

      <div className="p-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate mb-2">
          {product.name}
        </h3>

        <div className="flex items-baseline gap-2 mb-1">
          <span className="text-xl font-bold text-gray-900 dark:text-white">
            Rs. {product.salePrice?.toLocaleString() || 0}
          </span>
          {product.purchasePrice > 0 && (
            <span className="text-xs text-gray-400 dark:text-gray-500 line-through">
              Rs. {product.purchasePrice?.toLocaleString()}
            </span>
          )}
        </div>

        {hasPackaging && (
          <div className="flex items-center gap-1 mb-1.5 text-[10px] text-gray-400 dark:text-gray-500">
            <Box size={10} />
            <span>{product.piecesPerPack}/pk, {product.packsPerBox}/box</span>
          </div>
        )}
        <div className="flex items-center justify-between mb-2">
          <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${getProfitColor(profitPercent)}`}>
            <TrendingDown size={10} />
            {profitPercent.toFixed(0)}% margin
          </div>

          <span className={`text-xs font-medium ${stockLevel.text}`} title={readableStock || `${stock} pieces`}>
            {shortStock || `${stock} piece${stock !== 1 ? 's' : ''}`}
          </span>
        </div>

        <div className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs ${stockLevel.bg}`}>
          <span className={`font-medium ${stockLevel.text}`}>{stockLevel.label}</span>
          {product.minStock > 0 && (
            <span className="text-gray-400 dark:text-gray-500">
              Min: {product.minStock}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}
